// paex-clickable-promo.js — <paex-cpb-rotator> for sections/pe-clickable-promo-banner.liquid.
// Emitted (defer) ONLY while the section rotates (rotate_enabled + an image on
// both banners); the single-banner section never loads it. No libraries.
//
// Rotation (Sasha 2026-10-06, "like Google display ads"): both banners sit in
// one grid cell (.paex-cpb__stack, CSS) and cross-fade every data-interval
// seconds. Exactly one slide is active; the other is aria-hidden + inert (not
// focusable, not clickable, not announced). Each slide keeps its own CTA
// (<paex-apply-discount> / <custom-copy-text> / <a>) and "See details" popup —
// this element only switches which one is shown.
//
// Clock: a light heartbeat (every BEAT_MS) counts UNBLOCKED time; the next
// banner shows once a full interval has accumulated. Anything that blocks
// resets the count to zero, so the shopper always gets a FULL interval after
// an interaction ends — a switch never lands the instant a popup or toast
// closes.
//   Hard holds (event-driven; the heartbeat stops entirely while any is set):
//     hover     — a mouse/pen pointer is over the banner (touch excluded:
//                 touch has no reliable leave event);
//     touch     — a finger is down on the banner (pointerdown → up/cancel);
//     focus     — keyboard (:focus-visible) focus is inside the banner (the
//                 pause button excluded, so "Play" works while focused on it
//                 — APG carousel; click/tap focus is covered by hover/touch);
//     user      — the shopper pressed pause (or prefers reduced motion);
//     hidden    — the tab is in the background;
//     offscreen — the banner is scrolled out of view.
//   Soft holds (checked on each beat — all cheap: live collections or the
//   section's own small subtree): this section's legal popup is open, a promo
//   toast is on screen (paex-apply-discount toast or the native copy toast),
//   an apply request is in flight, or an overlay opened FROM a slide (cart
//   drawer after "apply", any modal) is still open — its close returns focus
//   to the opener, which must not have gone inert (WCAG 2.4.3).
//
// Banner 2 ships without src/srcset (data-src / data-srcset): a hidden slide
// still has a layout box in the viewport, so the lazy loader would fetch it
// during initial load and compete with banner 1 / LCP. The rotator sets the
// real sources after window load (or right before the first switch, if that
// comes earlier) and decode()s them.
//
// prefers-reduced-motion: never auto-rotates — starts paused (banner 1); the
// pause/play button (when the merchant shows it) lets the shopper opt in, and
// the CSS drops the fade.
(function () {
  'use strict';
  if (customElements.get('paex-cpb-rotator')) return;

  var MIN_S = 3;
  var MAX_S = 20;
  var BEAT_MS = 250;
  var DECODE_TIMEOUT_MS = 2500;

  // Live collections: a length read per beat, no DOM walk.
  var applyToasts = document.getElementsByClassName('paex-apply-toast');

  function toastOpen() {
    if (applyToasts.length) return true;
    var native = document.getElementById('toasts-container'); // global.js popToast (copy mode)
    return !!(native && native.firstElementChild);
  }

  // Give a deferred slide its real sources (<source> first, then <img>, so
  // the <picture> selects once). No-op for banner 1 (no data-* attributes).
  function hydrateSlide(slide) {
    var deferred = slide.querySelectorAll('[data-srcset], [data-src]');
    Array.prototype.forEach.call(deferred, function (el) {
      if (el.hasAttribute('data-srcset')) {
        el.setAttribute('srcset', el.getAttribute('data-srcset'));
        el.removeAttribute('data-srcset');
      }
      if (el.hasAttribute('data-src')) {
        el.setAttribute('src', el.getAttribute('data-src'));
        el.removeAttribute('data-src');
      }
    });
  }

  // Resolve when the slide's rendered image is decoded (or failed / timed
  // out) so the fade never reveals a half-painted banner. Only an image that
  // has not loaded yet is switched to eager — banner 1's attributes are never
  // touched once it has rendered.
  function decodeSlide(slide) {
    var img = slide && slide.querySelector('img');
    if (!img) return Promise.resolve();
    hydrateSlide(slide);
    if (!img.complete && img.loading === 'lazy') img.loading = 'eager';
    if (typeof img.decode !== 'function') return Promise.resolve();
    return new Promise(function (resolve) {
      var done = false;
      var finish = function () {
        if (done) return;
        done = true;
        resolve();
      };
      setTimeout(finish, DECODE_TIMEOUT_MS);
      img.decode().then(finish, finish);
    });
  }

  customElements.define(
    'paex-cpb-rotator',
    class PaexCpbRotator extends HTMLElement {
      connectedCallback() {
        var stack = this.querySelector('.paex-cpb__stack');
        this.slides = stack ? Array.prototype.slice.call(stack.children).filter(function (el) {
          return el.classList.contains('paex-cpb__slide');
        }) : [];
        if (this.slides.length < 2 || this.started) return;
        this.started = true;

        var seconds = parseInt(this.getAttribute('data-interval'), 10);
        if (!(seconds >= MIN_S)) seconds = 6;
        this.intervalMs = Math.min(MAX_S, seconds) * 1000;

        this.section = this.closest('.paex-cpb');
        this.toggle = this.querySelector('.paex-cpb__toggle');
        this.index = Math.max(0, this.slides.findIndex(function (el) {
          return el.classList.contains('is-active');
        }));
        this.holds = {};
        this.beat = null;
        this.elapsed = 0;
        this.switching = false;
        this.onBeat = this.beatTick.bind(this);

        this.on = [];
        this.listen(this, 'pointerenter', this.onPointerEnter.bind(this));
        this.listen(this, 'pointerleave', this.onPointerLeave.bind(this));
        this.listen(this, 'pointerdown', this.onPointerDown.bind(this));
        this.listen(this, 'pointerup', this.onPointerUp.bind(this));
        this.listen(this, 'pointercancel', this.onPointerUp.bind(this));
        this.listen(this, 'focusin', this.onFocusIn.bind(this));
        this.listen(this, 'focusout', this.onFocusOut.bind(this));
        this.listen(document, 'visibilitychange', this.onVisibility.bind(this));
        if (this.toggle) {
          this.listen(this.toggle, 'click', this.onToggle.bind(this));
          this.toggle.hidden = false;
        }

        this.motion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
        if (this.motion) {
          this.onMotion = this.onMotionChange.bind(this);
          if (this.motion.addEventListener) this.motion.addEventListener('change', this.onMotion);
          else if (this.motion.addListener) this.motion.addListener(this.onMotion);
          if (this.motion.matches) this.holds.user = true;
        }

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            function (entries) {
              var entry = entries[entries.length - 1];
              this.setHold('offscreen', !entry.isIntersecting);
            }.bind(this)
          );
          this.observer.observe(this);
        }

        if (document.hidden) this.holds.hidden = true;

        // Banner 2 has no sources in the markup: set + decode them once the
        // page has finished loading, never competing with banner 1 / LCP.
        this.onLoad = function () {
          this.slides.forEach(function (slide, i) {
            if (i !== this.index) decodeSlide(slide);
          }, this);
        }.bind(this);
        if (document.readyState === 'complete') this.onLoad();
        else window.addEventListener('load', this.onLoad, { once: true });

        this.syncToggle();
        this.restart();
      }

      disconnectedCallback() {
        this.stop();
        (this.on || []).forEach(function (l) {
          l[0].removeEventListener(l[1], l[2]);
        });
        this.on = [];
        if (this.observer) this.observer.disconnect();
        if (this.motion && this.onMotion) {
          if (this.motion.removeEventListener) this.motion.removeEventListener('change', this.onMotion);
          else if (this.motion.removeListener) this.motion.removeListener(this.onMotion);
        }
        if (this.onLoad) window.removeEventListener('load', this.onLoad);
        this.started = false;
      }

      listen(target, type, handler) {
        target.addEventListener(type, handler);
        this.on.push([target, type, handler]);
      }

      // ---- clock ---------------------------------------------------------------
      held() {
        for (var key in this.holds) {
          if (this.holds[key]) return true;
        }
        return false;
      }

      softBlocked() {
        if (this.section && this.section.querySelector('.paex-cpb__legal[open]')) return true;
        if (this.querySelector('[aria-busy="true"]')) return true;
        // An overlay opened from a slide is still open: ModalDialog/Drawer
        // show(opener) stamps aria-expanded="true" on the opener (the CTA for
        // the cart drawer, the "See details" trigger for a popup) and hide()
        // hands focus back to it — that slide must stay active and focusable.
        if (this.querySelector('[aria-expanded="true"]')) return true;
        var drawer = document.getElementById('Cart-Drawer');
        if (drawer && drawer.hasAttribute('open') && drawer.openedBy && this.contains(drawer.openedBy)) return true;
        return toastOpen();
      }

      setHold(name, on) {
        var was = !!this.holds[name];
        this.holds[name] = !!on;
        if (was === !!on) return;
        this.restart(); // stops while held; a FULL interval once the last hold clears
      }

      // Zero the count; run the heartbeat only while nothing hard-holds it.
      restart() {
        this.elapsed = 0;
        if (this.held()) {
          this.stop();
          return;
        }
        if (!this.beat) {
          this.last = Date.now();
          this.beat = setInterval(this.onBeat, BEAT_MS);
        }
      }

      stop() {
        if (this.beat) clearInterval(this.beat);
        this.beat = null;
      }

      beatTick() {
        var now = Date.now();
        var delta = Math.min(now - this.last, BEAT_MS * 4); // throttled-timer guard
        this.last = now;
        if (this.held() || this.switching) return;
        if (this.softBlocked()) {
          this.elapsed = 0;
          return;
        }
        this.elapsed += delta;
        if (this.elapsed >= this.intervalMs) {
          this.elapsed = 0;
          this.advance();
        }
      }

      advance() {
        this.switching = true;
        var nextIndex = (this.index + 1) % this.slides.length;
        decodeSlide(this.slides[nextIndex]).then(
          function () {
            this.switching = false;
            if (!this.isConnected) return;
            // a hold may have landed while decoding — the next full interval decides
            if (this.held() || this.softBlocked()) return;
            this.show(nextIndex);
            this.elapsed = 0;
          }.bind(this)
        );
      }

      show(nextIndex) {
        // Safety net: never strand focus inside the slide that goes inert —
        // hand it to the incoming banner's CTA (or the pause button).
        var outgoing = this.slides[this.index];
        var focused = document.activeElement;
        var moveFocus = !!(outgoing && focused && focused !== document.body && outgoing.contains(focused));
        this.slides.forEach(function (slide, i) {
          var active = i === nextIndex;
          slide.classList.toggle('is-active', active);
          if (active) {
            slide.removeAttribute('aria-hidden');
            slide.inert = false;
            slide.removeAttribute('inert');
          } else {
            slide.setAttribute('aria-hidden', 'true');
            slide.inert = true;
            slide.setAttribute('inert', '');
          }
        });
        this.index = nextIndex;
        if (moveFocus) {
          var target = this.slides[nextIndex].querySelector('.paex-cpb__cta') || this.toggle;
          if (target && typeof target.focus === 'function') target.focus({ preventScroll: true });
        }
      }

      // ---- events --------------------------------------------------------------
      onPointerEnter(event) {
        if (event.pointerType === 'touch') return;
        this.setHold('hover', true);
      }

      onPointerLeave(event) {
        if (event.pointerType === 'touch') return;
        this.setHold('hover', false);
      }

      onPointerDown(event) {
        if (event.pointerType !== 'touch') return;
        if (this.toggle && this.toggle.contains(event.target)) return;
        this.setHold('touch', true);
      }

      onPointerUp(event) {
        if (event.pointerType !== 'touch') return;
        this.setHold('touch', false);
      }

      onFocusIn(event) {
        if (this.toggle && this.toggle.contains(event.target)) return;
        // Keyboard focus only: a mouse click / tap also focuses the CTA, and
        // that focus lingers until the shopper clicks elsewhere — hover/touch
        // already cover pointer users, so a lingering click-focus must not
        // freeze the rotation for good.
        var keyboard = true;
        try {
          keyboard = event.target.matches(':focus-visible');
        } catch (error) {
          /* no :focus-visible support — treat every focus as keyboard focus */
        }
        if (keyboard) this.setHold('focus', true);
      }

      onFocusOut(event) {
        var to = event.relatedTarget;
        if (to && this.contains(to) && !(this.toggle && this.toggle.contains(to))) return;
        this.setHold('focus', false);
      }

      onVisibility() {
        this.setHold('hidden', document.hidden);
      }

      onMotionChange() {
        if (this.motion && this.motion.matches) {
          this.holds.user = true;
          this.restart();
          this.syncToggle();
        }
      }

      onToggle(event) {
        event.preventDefault();
        if (this.holds.user) {
          // Explicit Play: the shopper is ON the control — drop the
          // interaction holds it would otherwise sit behind (APG carousel).
          this.holds.hover = false;
          this.holds.focus = false;
          this.holds.touch = false;
          this.holds.user = false;
        } else {
          this.holds.user = true;
        }
        this.syncToggle();
        this.restart();
      }

      syncToggle() {
        if (!this.toggle) return;
        var paused = !!this.holds.user;
        this.toggle.setAttribute('data-state', paused ? 'paused' : 'playing');
        var label = this.getAttribute(paused ? 'data-label-play' : 'data-label-pause');
        if (label) this.toggle.setAttribute('aria-label', label);
      }
    }
  );
})();
